import { motion } from "framer-motion";
import {
  QrCode, ShoppingBag, CreditCard, BarChart3,
  Users, Gift, Brain, Truck, ChefHat, Monitor, ShoppingCart,
  UserCog, MessageCircle, Instagram
} from "lucide-react";
import { useState } from "react";

const features = [
  { icon: QrCode, title: "QR Code nas Mesas", desc: "Clientes escaneiam e pedem direto do celular. Sem filas, sem erros.", category: "pedidos" },
  { icon: ShoppingBag, title: "Cardápio Digital", desc: "Personalizável com suas cores e logo. Categorias, variações e adicionais.", category: "pedidos" },
  { icon: Monitor, title: "PDV Completo", desc: "Ponto de venda integrado com cardápio, estoque e relatórios.", category: "pedidos" },
  { icon: ChefHat, title: "Painel da Cozinha", desc: "Pedidos em tempo real com detalhes, status e prioridade.", category: "pedidos" },
  { icon: CreditCard, title: "Pagamentos Integrados", desc: "PIX, cartão, dinheiro e MercadoPago. Cada restaurante com sua conta.", category: "financeiro" },
  { icon: ShoppingCart, title: "Integração iFood", desc: "Receba pedidos do iFood e sincronize seu cardápio automaticamente.", category: "integracoes" },
  { icon: Instagram, title: "Instagram Shop", desc: "Sincronize seu catálogo com o Instagram Shopping e venda pela rede social.", category: "integracoes" },
  { icon: Truck, title: "Delivery Próprio", desc: "Raio de entrega, taxas, entregadores e rastreamento em tempo real.", category: "pedidos" },
  { icon: BarChart3, title: "Relatórios Completos", desc: "Faturamento, ticket médio, custos e margem de lucro.", category: "financeiro" },
  { icon: Gift, title: "Fidelidade & Cupons", desc: "Pontos, cashback, cartão fidelidade e cupons automáticos.", category: "marketing" },
  { icon: Users, title: "CRM de Clientes", desc: "Histórico, frequência, segmentação e reengajamento automático.", category: "marketing" },
  { icon: Brain, title: "IA Integrada", desc: "Descrições automáticas, sugestões inteligentes e insights.", category: "integracoes" },
  { icon: MessageCircle, title: "WhatsApp Automático", desc: "Notificações de pedidos e atendimento direto pelo WhatsApp.", category: "integracoes" },
  { icon: UserCog, title: "Gestão de Equipe", desc: "Funcionários, ponto digital, permissões e produtividade.", category: "financeiro" },
];

const categories = [
  { key: "all", label: "Tudo" },
  { key: "pedidos", label: "Pedidos" },
  { key: "financeiro", label: "Financeiro" },
  { key: "marketing", label: "Marketing" },
  { key: "integracoes", label: "Integrações" },
];

const FeaturesSection = () => {
  const [activeCategory, setActiveCategory] = useState("all");

  const filtered = activeCategory === "all" ? features : features.filter(f => f.category === activeCategory);

  return (
    <section id="features" className="py-24 relative overflow-hidden">
      <div className="container relative z-10">
        <motion.div
          className="text-center mb-12"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/20 text-primary text-xs font-semibold uppercase tracking-wider mb-4">
            Funcionalidades
          </span>
          <h2 className="text-3xl md:text-5xl font-bold mt-3 mb-4">
            Tudo que seu restaurante <span className="text-gradient">precisa</span>
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto mb-8">
            Uma plataforma completa do cardápio ao financeiro.
          </p>

          {/* Category filter */}
          <div className="flex flex-wrap gap-2 justify-center">
            {categories.map(c => (
              <button
                key={c.key}
                onClick={() => setActiveCategory(c.key)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  activeCategory === c.key
                    ? "bg-primary text-primary-foreground shadow-glow"
                    : "bg-muted text-muted-foreground hover:text-foreground hover:bg-muted/80"
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>
        </motion.div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 max-w-6xl mx-auto">
          {filtered.map((f, i) => (
            <motion.div
              key={f.title}
              className="group relative p-6 rounded-2xl border border-border bg-card hover:border-primary/30 hover:shadow-glow transition-all duration-300 overflow-hidden"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.04 }}
              layout
            >
              <div className="absolute top-0 left-0 w-full h-0.5 bg-gradient-primary opacity-0 group-hover:opacity-100 transition-opacity" />
              <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
                <f.icon className="h-5 w-5 text-primary" />
              </div>
              <h3 className="font-display font-semibold text-sm mb-2">{f.title}</h3>
              <p className="text-xs text-muted-foreground leading-relaxed">{f.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FeaturesSection;
